import { Auth, Config, logger, RestApiError } from '@evac/shared'
import { watch, type Ref } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import * as views from '@/views'
import { getEndpoints } from '@/rest'
import configRoutes from './config'

let dashboardLoaded = false

/** Who `/api/events`, `/alarms` and `/notifications` answer. Unknown before login: then the forced load follows. */
const isStaff = () => Auth.validateUserRoles(Config.ALLOWED_ROLES)

export function resetDashboardLoaded() {
  dashboardLoaded = false
}

/**
 * Fetch the dashboard's resources. Used by the route guard and re-invoked after a
 * successful login (the guard runs at app mount, before auth, so its first attempt
 * 401s and is swallowed — see App.vue). Pass `force` to bypass the loaded flag.
 */
export async function loadDashboardData(force = false) {
  if (dashboardLoaded && !force) return
  try {
    const endpoints = getEndpoints()
    // Not waiting for the role, which may take its full timeout to come.
    const shared = Promise.all([endpoints.location.fetchAll(), endpoints.room.fetchAll(), endpoints.scanner.fetchAll(), endpoints.device.fetchAll(), endpoints.activity.fetchAll()])
    // External sees the device overview only: these three answer it 403, and a failed
    // guard would blank the page.
    const staffOnly = isStaff().then(async (staff) => {
      if (!staff) return true
      // Events live on the server until an operator removes them, and the socket only
      // announces new ones: without this a reload hides every open card. A failure must
      // not cost the dashboard its alarm buttons, so it only makes the next visit retry.
      const eventsLoaded = endpoints.event.fetchAll().then(
        () => true,
        (error: unknown) => {
          logger.error(error)
          return false
        }
      )
      // Alarm + notification feed the dashboard's alarm-trigger buttons and the
      // active-alarm message (subject/description/location); without them the
      // buttons don't render and fired notifications show no text.
      const [events] = await Promise.all([eventsLoaded, endpoints.alarm.fetchAll(), endpoints.notification.fetchAll()])
      return events
    })
    const [events] = await Promise.all([staffOnly, shared])
    dashboardLoaded = events
  } catch (error) {
    if (error instanceof RestApiError && error.isUnauthorized) {
      // onUnauthorized already called logout(), Auth Wall will show login
      return
    }
    throw error
  }
}

/**
 * The socket only announces changes, and a reconnect doesn't touch `isAuthenticated`, so
 * nothing else would catch what happened while it was down: presses, removals and low-battery
 * flips are missed, and after a server restart the old cards have uuids it no longer knows. A
 * failed fetch leaves things as they are, until the next reconnect.
 */
export function refetchOnReconnect(isOnline: Ref<boolean | undefined>, isAuthenticated: Ref<boolean | undefined>) {
  return watch(isOnline, async (online, wasOnline) => {
    if (!online || wasOnline || !isAuthenticated.value) return
    const endpoints = getEndpoints()
    endpoints.device.fetchAll().catch(logger.error)
    if (await isStaff()) endpoints.event.fetchAll().catch(logger.error)
  })
}

const routes = [
  {
    path: '/',
    name: 'dashboard',
    component: views.Dashboard,
    beforeEnter: () => loadDashboardData(),
  },
  {
    path: '/config',
    name: 'config',
    component: views.Config,
    children: configRoutes,
    beforeEnter: () => Auth.validateUserRoles(Config.ALLOWED_ROLES),
  },
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    redirect: { name: 'dashboard' },
  },
] satisfies RouteRecordRaw[]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.afterEach((to, from, failure) => {
  if (!failure) return
  router.push({ name: 'dashboard' })
})

export default router
